// S5 · Step 3 — the plant-map DRAFT MODEL (Module 02 §3.4), as pure data +
// pure functions so every rule the screen enforces is unit-testable without a
// DOM or a network.
//
// The draft lives in `onboarding_step_states.payload` for step 3 (§3.4 pt 1):
//   {facilities:[{name, code, timezone, address,
//                 storage_locations:[{name, code, parent_code}],
//                 departments:[{name, code,
//                               work_centers:[{name, code, template_ref,
//                                              machine_count}]}]}]}
// The editor adds a client-only `uid` per node (a stable React key that survives
// a code rename); `toCommitDraft` strips it, so the wire shape is exactly the
// `PlantMapDraft` contract and nothing client-only is ever persisted.
//
// Two mirrors of server rules live here, and neither is the decision:
//  - `draftProblems` mirrors the 422 DUPLICATE_CODE tree-scoped uniqueness rule
//    (facility code tenant-wide; department + work-center code per FACILITY —
//    a work-center code is unique across all departments of one facility) so the
//    commit button can refuse before the server has to.
//  - `diffPlan` mirrors the §3.4 diff-BY-CODE so the commit bar can show what
//    will be created, what will be adopted, and — decisively — which committed
//    nodes are ABSENT from the draft, because `confirm_deletions` must never be
//    ticked blind ("deletions are never implicit").
// The server re-decides both under its advisory lock and is the only authority.

import { SUPPORTED_LOCALES } from "@assessly/contracts/locales";
import type { components } from "@assessly/api-client";

type S = components["schemas"];

// ---- the editor's draft shape ----------------------------------------------

export interface DraftWorkCenter {
  /** Client-only stable React key; never sent to the API. */
  readonly uid: string;
  name: string;
  /**
   * The committed `name_i18n` bundle this node was LOADED from, or null for a
   * node that was never in the committed tree (added here, or read back from a
   * draft written before the bundle existed).
   *
   * `name` above is ONE string, resolved for the browser's locale. Sending only
   * that string cannot round-trip: the server used to paste it into the
   * TENANT's default locale slot, so loading translated ru → en and committing
   * pasted the English back over the Russian — a commit that edited nothing
   * rewrote every Russian work-center name on the design partner's seed. The
   * bundle travels with the node so the commit can echo back exactly what it
   * read (see `outgoingNameI18n`).
   */
  name_i18n: Readonly<Record<string, string>> | null;
  code: string;
  /** Pack work-center template ref (`wc.extruder`), or null when unset. */
  template_ref: string | null;
  /** Machines to generate on first commit (§3.4 machine precedence). */
  machine_count: number;
}

export interface DraftDepartment {
  readonly uid: string;
  name: string;
  code: string;
  work_centers: DraftWorkCenter[];
}

export interface DraftStorageLocation {
  readonly uid: string;
  name: string;
  code: string;
  /** Nests a rack under its warehouse (§3.4 pt 1); null = top level. */
  parent_code: string | null;
}

export interface DraftFacility {
  readonly uid: string;
  name: string;
  code: string;
  timezone: string;
  address: string | null;
  storage_locations: DraftStorageLocation[];
  departments: DraftDepartment[];
}

export interface PlantDraft {
  facilities: DraftFacility[];
}

let uidSeq = 0;

/** A fresh client-only node key. Never persisted, never sent. */
export function newUid(prefix: string): string {
  uidSeq += 1;
  return `${prefix}-${uidSeq}`;
}

// ---- reading the step payload ----------------------------------------------

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function str(source: Record<string, unknown>, key: string): string {
  const value = source[key];
  return typeof value === "string" ? value : "";
}

function nullableStr(source: Record<string, unknown>, key: string): string | null {
  const value = source[key];
  return typeof value === "string" && value.length > 0 ? value : null;
}

function nonNegativeInt(source: Record<string, unknown>, key: string): number {
  const value = source[key];
  if (typeof value !== "number" || !Number.isFinite(value)) return 0;
  return Math.max(0, Math.floor(value));
}

function array(source: Record<string, unknown>, key: string): unknown[] {
  const value = source[key];
  return Array.isArray(value) ? value : [];
}

/**
 * THE ONE NORMALISATION. Every `name_i18n` bundle that enters this module —
 * from the committed tree, from a persisted step payload, from anywhere —
 * passes through here, and every bundle that leaves for the wire is built from
 * one that did. It is byte-for-byte the compaction the server applies before it
 * stores a bundle (`plant-map.service.ts` `compactBundle`): labels are TRIMMED,
 * blank locales are dropped.
 *
 * It exists because the two sides normalised DIFFERENTLY, and the difference
 * was permanent. The client kept a label verbatim; the server trimmed it. So a
 * committed `{ru: "Волочение 1 "}` was held by the editor as
 * `{ru: "Волочение 1 "}` while the server, the moment it was committed, stored
 * `{ru: "Волочение 1"}` — and because the draft never re-read the bundle it had
 * just written, EVERY subsequent diff merged the stale trailing space back over
 * the stored label and reported an edit that did not exist. The commit surface
 * then could not go quiet no matter how many times Commit was pressed
 * (reproduced on a replica: three commits, `.plant-commit` still on screen,
 * "1 existing node is updated in place: Волочение 1" every time).
 *
 * Normalising at ONE place, on both sides of the comparison, is what makes the
 * load/commit → diff identity hold by construction instead of by two sides
 * happening to agree on what a label looks like.
 */
export function normalizeNameI18n(
  bundle: Readonly<Record<string, string | undefined>>,
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [locale, label] of Object.entries(bundle)) {
    if (typeof label !== "string") continue;
    const trimmed = label.trim();
    if (trimmed.length > 0) out[locale] = trimmed;
  }
  return out;
}

/** Read a `name_i18n` bundle out of a persisted payload, NORMALISED. A payload
 * with no bundle at all — an agent-written draft, or one saved before the
 * bundle existed — reads back as null, which `outgoingNameI18n` treats as
 * "authored here". */
function nameBundle(source: Record<string, unknown>): Record<string, string> | null {
  const value = source["name_i18n"];
  if (!isRecord(value)) return null;
  const out = normalizeNameI18n(value as Record<string, string | undefined>);
  return Object.keys(out).length > 0 ? out : null;
}

/**
 * Read a step-3 payload into the editor's draft. Deliberately total: a payload
 * written by an older build (or by an agent) never throws and never drops the
 * user into an error screen — unknown members are ignored, missing members
 * become empty, and the user can fix the tree by hand.
 */
export function parseStepPayload(payload: unknown): PlantDraft {
  if (!isRecord(payload)) return { facilities: [] };
  return {
    facilities: array(payload, "facilities")
      .filter(isRecord)
      .map((f) => ({
        uid: newUid("f"),
        name: str(f, "name"),
        code: str(f, "code"),
        timezone: str(f, "timezone"),
        address: nullableStr(f, "address"),
        storage_locations: array(f, "storage_locations")
          .filter(isRecord)
          .map((l) => ({
            uid: newUid("l"),
            name: str(l, "name"),
            code: str(l, "code"),
            parent_code: nullableStr(l, "parent_code"),
          })),
        departments: array(f, "departments")
          .filter(isRecord)
          .map((d) => ({
            uid: newUid("d"),
            name: str(d, "name"),
            code: str(d, "code"),
            work_centers: array(d, "work_centers")
              .filter(isRecord)
              .map((w) => ({
                uid: newUid("w"),
                name: str(w, "name"),
                name_i18n: nameBundle(w),
                code: str(w, "code"),
                template_ref: nullableStr(w, "template_ref"),
                machine_count: nonNegativeInt(w, "machine_count"),
              })),
          })),
      })),
  };
}

// ---- localized work-center names -------------------------------------------

/** The locales the platform ships, in the registry's FIXED order, so the
 * fallback below is deterministic and never depends on jsonb key order. */
const LOCALE_ORDER = SUPPORTED_LOCALES;

/**
 * The one string the editor shows for a `name_i18n` bundle.
 *
 * The chain is: the requested locale, then the platform locales in order, then
 * anything else the bundle carries, and only then the code. The tail matters:
 * a work center created by a Russian tenant carries `{ru}` and nothing else, so
 * an English-speaking admin used to see the CODE where its name should be —
 * and, before the round trip was fixed, then wrote that code back over the
 * Russian name. Showing a name in a language you did not ask for is a smaller
 * failure than showing no name at all.
 */
export function resolveLocalizedName(
  bundle: Readonly<Record<string, string | undefined>>,
  locale: string,
  code: string,
): string {
  const pick = (key: string): string | null => {
    const label = bundle[key];
    // An EMPTY label is as unusable as a missing one — keep falling through
    // rather than rendering a blank row.
    return label !== undefined && label.length > 0 ? label : null;
  };
  const chain = [locale, ...LOCALE_ORDER, ...Object.keys(bundle).sort()];
  for (const key of chain) {
    const label = pick(key);
    if (label !== null) return label;
  }
  return code;
}

/**
 * The `name_i18n` the commit sends for one draft work center.
 *
 * Three cases, and the first is the whole fix:
 *  - name UNCHANGED from what the loader resolved ⇒ the loaded bundle, byte for
 *    byte. The server merges it over the committed bundle, sees no difference,
 *    and writes nothing. That is the load → commit-unchanged identity, and it
 *    holds by construction rather than by two sides agreeing on a locale rule.
 *  - name EDITED ⇒ the loaded bundle with the AUTHORING locale's slot replaced.
 *    Editing the English name of a Russian line adds an English name; it does
 *    not overwrite the Russian one.
 *  - node never loaded from the committed tree ⇒ a fresh single-slot bundle in
 *    the authoring locale. Honest: that is the only language it has a name in.
 */
export function outgoingNameI18n(
  wc: DraftWorkCenter,
  locale: string,
): Readonly<Record<string, string>> {
  const name = wc.name.trim();
  // Normalised on the way OUT as well as on the way in, so the bytes the server
  // compares are the bytes this module compares — including for a draft node
  // built before `normalizeNameI18n` existed, or handed in by a caller that
  // skipped the readers above.
  const bundle = wc.name_i18n === null ? null : normalizeNameI18n(wc.name_i18n);
  if (bundle === null) return name.length > 0 ? { [locale]: name } : {};
  if (resolveLocalizedName(bundle, locale, wc.code.trim()) === name) return bundle;
  // A BLANK name never claims a slot (the server compacts blanks away too, and
  // an empty name is a blocking draft problem long before this runs).
  return name.length > 0 ? { ...bundle, [locale]: name } : bundle;
}

/** Same locales, same labels — key ORDER is not a difference (jsonb loses it). */
export function sameNameI18n(
  a: Readonly<Record<string, string>>,
  b: Readonly<Record<string, string>>,
): boolean {
  const keys = Object.keys(a);
  if (keys.length !== Object.keys(b).length) return false;
  return keys.every((k) => a[k] === b[k]);
}

/**
 * The wire draft (`PlantMapDraft`): trimmed, client keys stripped, optional
 * members omitted when unset.
 *
 * `address` is omitted when blank ON PURPOSE — §3.4: "an omitted address must
 * not null a DB value set elsewhere", so a draft that never carried an address
 * leaves the committed one alone.
 *
 * `locale` is the locale the NAMES IN THIS DRAFT WERE AUTHORED IN — the
 * browser's UI locale, which is the client's own fact and the only side that
 * knows it. It decides which slot an edited work-center name lands in.
 */
export function toCommitDraft(draft: PlantDraft, locale: string): S["PlantMapDraft"] {
  return {
    facilities: draft.facilities.map((f) => ({
      name: f.name.trim(),
      code: f.code.trim(),
      timezone: f.timezone.trim(),
      ...(f.address !== null && f.address.trim().length > 0 ? { address: f.address.trim() } : {}),
      storage_locations: f.storage_locations.map((l) => ({
        name: l.name.trim(),
        code: l.code.trim(),
        ...(l.parent_code !== null && l.parent_code.trim().length > 0
          ? { parent_code: l.parent_code.trim() }
          : {}),
      })),
      departments: f.departments.map((d) => ({
        name: d.name.trim(),
        code: d.code.trim(),
        work_centers: d.work_centers.map((w) => {
          const nameI18n = outgoingNameI18n(w, locale);
          return {
            name: w.name.trim(),
            code: w.code.trim(),
            ...(Object.keys(nameI18n).length > 0 ? { name_i18n: nameI18n } : {}),
            ...(w.template_ref !== null && w.template_ref.trim().length > 0
              ? { template_ref: w.template_ref.trim() }
              : {}),
            ...(w.machine_count > 0 ? { machine_count: w.machine_count } : {}),
          };
        }),
      })),
    })),
  };
}

/** The step payload we persist (the wire draft — no client-only members). The
 * bundle is persisted WITH the draft, so a save/reload cycle does not launder
 * the tenant's own names through the browser's locale. */
export function toStepPayload(draft: PlantDraft, locale: string): Record<string, unknown> {
  return toCommitDraft(draft, locale);
}

// ---- validation (client mirror of 422 DUPLICATE_CODE + required fields) -----

export type DraftProblemCode =
  | "facility_code_required"
  | "facility_name_required"
  | "facility_timezone_required"
  | "department_code_required"
  | "department_name_required"
  | "work_center_code_required"
  | "work_center_name_required"
  | "storage_code_required"
  | "storage_name_required"
  | "duplicate_facility_code"
  | "duplicate_department_code"
  | "duplicate_work_center_code"
  | "duplicate_storage_code"
  | "unknown_storage_parent"
  | "work_center_moved";

export interface DraftProblem {
  readonly code: DraftProblemCode;
  /** `FAC/DEPT/WC` — the same path shape the API's refusals use. */
  readonly path: string;
  /** Blocking problems disable the commit; warnings only inform. */
  readonly severity: "error" | "warning";
  /**
   * The DOM id of the offending input (`${uid}-code`), so the problems panel can
   * FOCUS it instead of leaving the user to hunt for "duplicate code WC-01" in a
   * 21-work-center tree, and so the input can point back with `aria-describedby`.
   * Empty when the problem is not about one field.
   */
  readonly fieldId: string;
}

function push(list: DraftProblem[], code: DraftProblemCode, path: string, fieldId: string): void {
  list.push({
    code,
    path,
    fieldId,
    severity: code === "unknown_storage_parent" ? "warning" : "error",
  });
}

/** Stable DOM id for one editable field of one draft node. */
export function fieldDomId(uid: string, field: "code" | "name" | "tz"): string {
  return `${uid}-${field}`;
}

/** Every problem attached to a given field id, for `aria-invalid` on the input. */
export function problemsForField(
  problems: readonly DraftProblem[],
  fieldId: string,
): DraftProblem[] {
  return problems.filter((p) => p.fieldId === fieldId);
}

/**
 * Everything wrong with the draft, in one pass: blank required fields and the
 * tree-scoped duplicate codes the server answers 422 `DUPLICATE_CODE` for.
 * Storage-location duplicates are included because Module 06 keys locations
 * `(facility, code)` (§3.4) — committing two identical codes would collide the
 * moment that module wires in.
 */
export function draftProblems(draft: PlantDraft): DraftProblem[] {
  const problems: DraftProblem[] = [];
  const facilityCodes = new Set<string>();

  for (const f of draft.facilities) {
    const fCode = f.code.trim();
    const fPath = fCode.length > 0 ? fCode : "?";
    if (fCode.length === 0) {
      push(problems, "facility_code_required", fPath, fieldDomId(f.uid, "code"));
    }
    if (f.name.trim().length === 0) {
      push(problems, "facility_name_required", fPath, fieldDomId(f.uid, "name"));
    }
    if (f.timezone.trim().length === 0) {
      push(problems, "facility_timezone_required", fPath, fieldDomId(f.uid, "tz"));
    }
    if (fCode.length > 0) {
      if (facilityCodes.has(fCode)) {
        push(problems, "duplicate_facility_code", fPath, fieldDomId(f.uid, "code"));
      }
      facilityCodes.add(fCode);
    }

    const storageCodes = new Set<string>();
    for (const l of f.storage_locations) {
      const lCode = l.code.trim();
      const lPath = `${fPath}/${lCode.length > 0 ? lCode : "?"}`;
      if (lCode.length === 0) {
        push(problems, "storage_code_required", lPath, fieldDomId(l.uid, "code"));
      }
      if (l.name.trim().length === 0) {
        push(problems, "storage_name_required", lPath, fieldDomId(l.uid, "name"));
      }
      if (lCode.length > 0) {
        if (storageCodes.has(lCode)) {
          push(problems, "duplicate_storage_code", lPath, fieldDomId(l.uid, "code"));
        }
        storageCodes.add(lCode);
      }
    }
    for (const l of f.storage_locations) {
      const parent = l.parent_code?.trim() ?? "";
      if (parent.length > 0 && !storageCodes.has(parent)) {
        push(
          problems,
          "unknown_storage_parent",
          `${fPath}/${l.code.trim()}`,
          fieldDomId(l.uid, "code"),
        );
      }
    }

    const deptCodes = new Set<string>();
    // Work-center codes are unique per FACILITY, not per department — the same
    // UNIQUE (tenant_id, facility_id, code) index the API mirrors (§3.4).
    const wcCodes = new Set<string>();
    for (const d of f.departments) {
      const dCode = d.code.trim();
      const dPath = `${fPath}/${dCode.length > 0 ? dCode : "?"}`;
      if (dCode.length === 0) {
        push(problems, "department_code_required", dPath, fieldDomId(d.uid, "code"));
      }
      if (d.name.trim().length === 0) {
        push(problems, "department_name_required", dPath, fieldDomId(d.uid, "name"));
      }
      if (dCode.length > 0) {
        if (deptCodes.has(dCode)) {
          push(problems, "duplicate_department_code", dPath, fieldDomId(d.uid, "code"));
        }
        deptCodes.add(dCode);
      }
      for (const w of d.work_centers) {
        const wCode = w.code.trim();
        const wPath = `${dPath}/${wCode.length > 0 ? wCode : "?"}`;
        if (wCode.length === 0) {
          push(problems, "work_center_code_required", wPath, fieldDomId(w.uid, "code"));
        }
        if (w.name.trim().length === 0) {
          push(problems, "work_center_name_required", wPath, fieldDomId(w.uid, "name"));
        }
        if (wCode.length > 0) {
          if (wcCodes.has(wCode)) {
            push(problems, "duplicate_work_center_code", wPath, fieldDomId(w.uid, "code"));
          }
          wcCodes.add(wCode);
        }
      }
    }
  }
  return problems;
}

// ---- subtree size, for a removal the user can weigh --------------------------

export interface SubtreeCounts {
  readonly departments: number;
  readonly workCenters: number;
  readonly storageLocations: number;
  readonly machines: number;
}

/** What removing this facility would take with it (§3.4 "deletions are never
 * implicit" — the draft deserves the same care as the committed tree). */
export function facilitySubtreeCounts(facility: DraftFacility): SubtreeCounts {
  const workCenters = facility.departments.flatMap((d) => d.work_centers);
  return {
    departments: facility.departments.length,
    workCenters: workCenters.length,
    storageLocations: facility.storage_locations.length,
    machines: workCenters.reduce((n, w) => n + w.machine_count, 0),
  };
}

export function departmentSubtreeCounts(department: DraftDepartment): SubtreeCounts {
  return {
    departments: 0,
    workCenters: department.work_centers.length,
    storageLocations: 0,
    machines: department.work_centers.reduce((n, w) => n + w.machine_count, 0),
  };
}

/** True when removing this node would take other nodes with it. */
export function hasChildren(counts: SubtreeCounts): boolean {
  return counts.departments + counts.workCenters + counts.storageLocations + counts.machines > 0;
}

/** `n` code-numbered work-center stubs (`EXT-01` … `EXT-04`) for the bulk add. */
export function bulkWorkCenters(
  prefix: string,
  count: number,
  existing: readonly DraftWorkCenter[],
): DraftWorkCenter[] {
  const taken = new Set(existing.map((w) => w.code.trim().toUpperCase()));
  const base = prefix.trim().toUpperCase().length > 0 ? prefix.trim().toUpperCase() : "WC";
  const out: DraftWorkCenter[] = [];
  let n = 1;
  while (out.length < count) {
    const code = `${base}-${String(n).padStart(2, "0")}`;
    n += 1;
    if (taken.has(code)) continue;
    taken.add(code);
    out.push({ ...emptyWorkCenter(), code, name: code });
  }
  return out;
}

/** True when nothing blocks a commit (warnings do not). */
export function isCommittable(problems: readonly DraftProblem[]): boolean {
  return !problems.some((p) => p.severity === "error");
}

// ---- diff against the committed tree (§3.4 diff-by-code) --------------------

/** The committed org tree, as the three Module 16 list endpoints return it. */
export interface ExistingTree {
  readonly facilities: readonly S["Facility"][];
  readonly departments: readonly S["Department"][];
  readonly workCenters: readonly S["WorkCenter"][];
}

export const EMPTY_TREE: ExistingTree = { facilities: [], departments: [], workCenters: [] };

export type PlantNodeKind = "facility" | "department" | "work_center";

export interface PlantNodeRef {
  readonly kind: PlantNodeKind;
  /** `FAC` / `FAC/DEPT` / `FAC/DEPT/WC`, matching the API's refusal paths. */
  readonly path: string;
  readonly name: string;
}

export interface PlantDiff {
  /** Nodes whose code has no committed match AT ALL — the commit CREATES them. */
  readonly create: {
    readonly facilities: number;
    readonly departments: number;
    readonly work_centers: number;
    readonly storage_locations: number;
  };
  /**
   * Nodes whose code matches an ARCHIVED committed row. §3.4's server matches by
   * code across every status and un-archives what it finds (`plant-map.service`:
   * `status !== "active"` → `{status:"active"}`, `result.updated++`), so these
   * are RESTORES, not creations, and the server counts them under `updated`.
   *
   * Recycling a decommissioned line's code is ordinary factory practice. While
   * the client read only `status=active` rows it could not see the collision, so
   * it predicted "will create 1" and then reported "Committed 1 new nodes" for a
   * commit whose own 200 body said `{created:{work_centers:0}, updated:1}` — the
   * screen stating two different outcomes for one commit (UX-1 surface review
   * F2). The committed-tree read now fetches every status and this bucket is the
   * classification that keeps the client's prediction and the server's answer
   * the same sentence.
   */
  readonly restore: {
    readonly facilities: number;
    readonly departments: number;
    readonly work_centers: number;
  };
  /**
   * Entities the commit RECORDS but cannot create, because their owning module
   * is not wired into this deployment (machines → Module 08, §3.4). The server
   * reports them under `pending[]` and never under `created`, so they are NOT a
   * change to the committed plant: a draft that differs from the committed tree
   * only by machine counts has nothing to commit, and the machine counts live on
   * (and are saved with) the step draft until Module 08 ships.
   *
   * Kept out of `create` deliberately. While machines were summed into `create`,
   * `isNoOpDiff` could never return true on any tenant whose tree carries
   * machine counts, so the commit surface stayed on screen forever after a
   * successful commit reading "Nothing to commit yet." beside "Validated · no
   * conflicts" and a dead Commit button (doctrine review B1).
   */
  readonly deferred: {
    readonly machines: number;
  };
  /** Draft nodes adopted by code (the commit updates them only if changed). */
  readonly matched: {
    readonly facilities: number;
    readonly departments: number;
    readonly work_centers: number;
  };
  /**
   * Matched nodes the commit will UPDATE IN PLACE: a renamed facility,
   * department or work center, or a facility whose timezone moved. The server
   * counts these under `updated`.
   *
   * The diff used to match by code and never look at a name, so a draft whose
   * only edit was a rename scored `isNoOpDiff` — the commit surface never
   * rendered, and the rename could not be committed AT ALL. It was also how the
   * screen came to say "nothing to commit" over a commit that rewrote 13 work
   * centers' Russian names: the client could not predict, warn about, or even
   * display the write it was about to perform.
   */
  readonly edited: readonly PlantNodeRef[];
  /**
   * ACTIVE committed nodes absent from the draft. With the default
   * `confirm_deletions:false` these are left untouched; ticking the confirm box
   * asks the server to archive them (or refuse with 409 ENTITY_IN_USE). The
   * screen must show this list before the box can be ticked.
   */
  readonly absent: readonly PlantNodeRef[];
  /**
   * Work centers the draft moved to a different department than the committed
   * tree has. §3.4 adopts an existing work center BY CODE and updates its name
   * only — the re-parent is NOT applied by a plant-map commit, so saying so is
   * the honest thing to do rather than showing a move that will not happen.
   */
  readonly reparented: readonly PlantNodeRef[];
  /**
   * The draft nodes the commit will CREATE (no committed match by code), named
   * — the §3.7 commit-bar sentence and the §3.8 success banner both name what
   * changes instead of only counting it. Facilities/departments/work centers
   * only: storage locations are counted in `create` but live in Module 06 and
   * have no tree node to link back to.
   */
  readonly createdNodes: readonly PlantNodeRef[];
  /** The draft nodes the commit will RESTORE from archived, named — the sentence
   * and the success record both say "restore", never "create", about these. */
  readonly restoredNodes: readonly PlantNodeRef[];
}

function activeOnly<T extends { status: "active" | "archived" }>(rows: readonly T[]): T[] {
  return rows.filter((r) => r.status === "active");
}

/**
 * Diff the draft against the committed tree exactly the way §3.4 does: by code,
 * facility tenant-wide, department per facility, work center per FACILITY —
 * matching across EVERY status, because the server does (an archived row whose
 * code the draft reuses is un-archived and updated, not created) — and, for a
 * matched node, comparing the values the commit would write.
 *
 * `locale` is the locale the draft's names were authored in (the browser's), so
 * the work-center comparison can be made against the same merged bundle the
 * server will store. See `outgoingNameI18n`.
 */
export function diffPlan(draft: PlantDraft, existing: ExistingTree, locale: string): PlantDiff {
  const facilities = activeOnly(existing.facilities);
  const departments = activeOnly(existing.departments);
  const workCenters = activeOnly(existing.workCenters);

  // ACTIVE rows decide "matched vs absent"; ALL rows decide "create vs restore".
  const facilityByCode = new Map(facilities.map((f) => [f.code, f]));
  const archivedFacilityByCode = new Map(
    existing.facilities.filter((f) => f.status !== "active").map((f) => [f.code, f]),
  );
  const create = {
    facilities: 0,
    departments: 0,
    work_centers: 0,
    storage_locations: 0,
  };
  const restore = { facilities: 0, departments: 0, work_centers: 0 };
  const deferred = { machines: 0 };
  const matched = { facilities: 0, departments: 0, work_centers: 0 };
  const edited: PlantNodeRef[] = [];
  const reparented: PlantNodeRef[] = [];
  const createdNodes: PlantNodeRef[] = [];
  const restoredNodes: PlantNodeRef[] = [];

  // Committed codes seen in the draft, so the leftovers are the absent nodes.
  const seenFacilities = new Set<string>();
  const seenDepartments = new Set<string>();
  const seenWorkCenters = new Set<string>();

  for (const f of draft.facilities) {
    const code = f.code.trim();
    if (code.length === 0) continue;
    const exf = facilityByCode.get(code);
    const archivedF = exf ? undefined : archivedFacilityByCode.get(code);
    if (exf) {
      matched.facilities += 1;
      seenFacilities.add(exf.id);
      // The same two fields the server's `facilityChanged` compares. `address`
      // is deliberately not one of them: the editor has no address field, so
      // the wire draft omits it and §3.4 leaves the committed value alone.
      if (exf.name !== f.name.trim() || exf.timezone !== f.timezone.trim()) {
        edited.push({ kind: "facility", path: code, name: f.name.trim() });
      }
    } else if (archivedF) {
      restore.facilities += 1;
      restoredNodes.push({ kind: "facility", path: code, name: f.name.trim() });
    } else {
      create.facilities += 1;
      createdNodes.push({ kind: "facility", path: code, name: f.name.trim() });
    }
    create.storage_locations += f.storage_locations.length;

    // Children are read under whichever committed facility the code resolves to
    // — active or archived — so a restored facility's own archived children are
    // classified as restores too, exactly as the server will treat them.
    const parentF = exf ?? archivedF;
    const exDepts = parentF ? departments.filter((d) => d.facility_id === parentF.id) : [];
    const exDeptByCode = new Map(exDepts.map((d) => [d.code, d]));
    const archivedDeptByCode = parentF
      ? new Map(
          existing.departments
            .filter((d) => d.status !== "active" && d.facility_id === parentF.id)
            .map((d) => [d.code, d]),
        )
      : new Map<string, S["Department"]>();
    const exWcs = parentF ? workCenters.filter((w) => w.facility_id === parentF.id) : [];
    const exWcByCode = new Map(exWcs.map((w) => [w.code, w]));
    const archivedWcByCode = parentF
      ? new Map(
          existing.workCenters
            .filter((w) => w.status !== "active" && w.facility_id === parentF.id)
            .map((w) => [w.code, w]),
        )
      : new Map<string, S["WorkCenter"]>();

    for (const d of f.departments) {
      const dCode = d.code.trim();
      if (dCode.length === 0) continue;
      const exd = exDeptByCode.get(dCode);
      const archivedD = exd ? undefined : archivedDeptByCode.get(dCode);
      if (exd) {
        matched.departments += 1;
        seenDepartments.add(exd.id);
        if (exd.name !== d.name.trim()) {
          edited.push({ kind: "department", path: `${code}/${dCode}`, name: d.name.trim() });
        }
      } else if (archivedD) {
        restore.departments += 1;
        restoredNodes.push({ kind: "department", path: `${code}/${dCode}`, name: d.name.trim() });
      } else {
        create.departments += 1;
        createdNodes.push({
          kind: "department",
          path: `${code}/${dCode}`,
          name: d.name.trim(),
        });
      }

      for (const w of d.work_centers) {
        const wCode = w.code.trim();
        if (wCode.length === 0) continue;
        // Recorded, never created (Module 08 unwired) — counted for the honest
        // "recorded, not created yet" line, never as a change to commit.
        deferred.machines += w.machine_count;
        const exw = exWcByCode.get(wCode);
        if (!exw) {
          const archivedW = archivedWcByCode.get(wCode);
          if (archivedW) {
            restore.work_centers += 1;
            restoredNodes.push({
              kind: "work_center",
              path: `${code}/${dCode}/${wCode}`,
              name: w.name.trim(),
            });
            continue;
          }
          create.work_centers += 1;
          createdNodes.push({
            kind: "work_center",
            path: `${code}/${dCode}/${wCode}`,
            name: w.name.trim(),
          });
          continue;
        }
        matched.work_centers += 1;
        seenWorkCenters.add(exw.id);
        // Compared as the server compares it: the committed bundle against the
        // MERGE the commit would store, never one locale slot against a string
        // resolved for a different one.
        const committedBundle = committedNameI18n(exw);
        const merged = { ...committedBundle, ...outgoingNameI18n(w, locale) };
        if (!sameNameI18n(committedBundle, merged)) {
          edited.push({
            kind: "work_center",
            path: `${code}/${dCode}/${wCode}`,
            name: w.name.trim(),
          });
        }
        if (exd !== undefined && exw.department_id !== exd.id) {
          reparented.push({
            kind: "work_center",
            path: `${code}/${dCode}/${wCode}`,
            name: w.name,
          });
        }
      }
    }
  }

  const absent: PlantNodeRef[] = [];
  for (const f of facilities) {
    if (seenFacilities.has(f.id)) continue;
    absent.push({ kind: "facility", path: f.code, name: f.name });
  }
  for (const d of departments) {
    if (seenDepartments.has(d.id)) continue;
    const facility = facilities.find((f) => f.id === d.facility_id);
    if (!facility || !seenFacilities.has(facility.id)) continue; // reported at facility level
    absent.push({ kind: "department", path: `${facility.code}/${d.code}`, name: d.name });
  }
  for (const w of workCenters) {
    if (seenWorkCenters.has(w.id)) continue;
    // §3.4: a facility-level work center (no department) is not representable in
    // the draft tree, so it is never treated as a deletion.
    if (w.department_id === null) continue;
    const facility = facilities.find((f) => f.id === w.facility_id);
    const department = departments.find((d) => d.id === w.department_id);
    if (!facility || !department) continue;
    if (!seenFacilities.has(facility.id) || !seenDepartments.has(department.id)) continue;
    absent.push({
      kind: "work_center",
      path: `${facility.code}/${department.code}/${w.code}`,
      name: w.code,
    });
  }

  return {
    create,
    restore,
    deferred,
    matched,
    edited,
    absent,
    reparented,
    createdNodes,
    restoredNodes,
  };
}

/** Everything the commit would CHANGE about the committed plant: creations,
 * restores of archived rows, in-place edits, and archives on offer. `deferred`
 * is deliberately absent — machines are recorded pending, never created, so a
 * draft that differs only by machine count changes nothing (doctrine review
 * B1). `edited` IS counted: a rename is a write, so hiding the commit surface
 * for it left the user with no way to commit one. */
export function changeCount(diff: PlantDiff): number {
  return (
    diff.create.facilities +
    diff.create.departments +
    diff.create.work_centers +
    diff.create.storage_locations +
    diff.restore.facilities +
    diff.restore.departments +
    diff.restore.work_centers +
    diff.edited.length +
    diff.absent.length
  );
}

/** True when the commit would change NOTHING — the §UX-1 rule that the commit
 * surface does not render until the draft differs from the committed plant. */
export function isNoOpDiff(diff: PlantDiff): boolean {
  return changeCount(diff) === 0;
}

/** True when the draft has no node at all — drives the S5 empty state. */
export function isEmptyDraft(draft: PlantDraft): boolean {
  return draft.facilities.length === 0;
}

// ---- seeding from the committed tree ----------------------------------------

/** Pick the tenant-visible work-center name out of its `name_i18n` bundle. */
export function workCenterName(wc: S["WorkCenter"], locale: string): string {
  return resolveLocalizedName(committedNameI18n(wc), locale, wc.code);
}

/** The committed bundle, through the ONE normalisation — the value the draft
 * carries and the commit echoes back. Normalising here rather than at commit
 * time is what makes the client and the server (which compacts identically)
 * agree on what "the committed bundle" IS: an `{en: ""}` row does not force an
 * update, and neither does a stray trailing space. */
export function committedNameI18n(wc: S["WorkCenter"]): Record<string, string> {
  return normalizeNameI18n(wc.name_i18n);
}

/**
 * ADOPT THE SERVER'S ANSWER. Re-base every draft work center's `name_i18n` on
 * the bundle the committed tree actually holds, matched by code within its
 * facility across every status — exactly the way the server matches.
 *
 * The user's `name` is NEVER touched: it is the one field they type into, and a
 * rename typed while a commit was in flight must survive. Only the BASE the
 * commit merges over is replaced, so the next diff asks the right question —
 * "does the name on screen still equal what the stored bundle resolves to?" —
 * instead of arguing with a bundle the server has already moved past.
 *
 * Without this the draft holds its pre-commit bundle forever. The server merges
 * (`{...existing, ...provided}`) and normalises, so a commit can legitimately
 * leave the stored bundle DIFFERENT from the one the client sent — one locale
 * richer, or one trailing space poorer. Every diff after that reports an edit
 * that will never be applied and the §UX-1 rule "the commit surface renders
 * only when the draft differs from the committed plant" becomes unsatisfiable.
 *
 * Returns the SAME draft reference when nothing changed, so the caller can feed
 * it straight to `setDraft` without re-rendering on every tree read.
 */
export function adoptCommittedNames(
  draft: PlantDraft,
  committed: {
    readonly facilities: readonly S["Facility"][];
    readonly workCenters: readonly S["WorkCenter"][];
  },
): PlantDraft {
  // "Nothing changed" is decided by REFERENCE at every level — a node that was
  // left alone is returned as itself, so the check is `===` rather than a
  // mutable flag threaded through three nested callbacks.
  const untouched = <T>(next: readonly T[], prev: readonly T[]): boolean =>
    next.every((item, i) => item === prev[i]);
  const facilityByCode = new Map(committed.facilities.map((f) => [f.code, f]));
  const facilities = draft.facilities.map((f) => {
    const exf = facilityByCode.get(f.code.trim());
    if (exf === undefined) return f;
    const wcByCode = new Map(
      committed.workCenters.filter((w) => w.facility_id === exf.id).map((w) => [w.code, w]),
    );
    const departments = f.departments.map((d) => {
      const work_centers = d.work_centers.map((w) => {
        const exw = wcByCode.get(w.code.trim());
        if (exw === undefined) return w;
        const bundle = committedNameI18n(exw);
        if (w.name_i18n !== null && sameNameI18n(w.name_i18n, bundle)) return w;
        return { ...w, name_i18n: bundle };
      });
      return untouched(work_centers, d.work_centers) ? d : { ...d, work_centers };
    });
    return untouched(departments, f.departments) ? f : { ...f, departments };
  });
  return untouched(facilities, draft.facilities) ? draft : { facilities };
}

/**
 * Build a draft from the COMMITTED tree, so a returning admin edits what the
 * factory actually has rather than an empty page (and so the diff summary is
 * "adopt 21 / create 0" instead of a phantom rebuild). Storage locations and
 * machines are not readable yet (Modules 06/08 unbound, §3.4), so they seed
 * empty — the screen says so rather than implying the tenant has none.
 */
export function draftFromExisting(existing: ExistingTree, locale: string): PlantDraft {
  const departments = activeOnly(existing.departments);
  const workCenters = activeOnly(existing.workCenters);
  return {
    facilities: activeOnly(existing.facilities).map((f) => ({
      uid: newUid("f"),
      name: f.name,
      code: f.code,
      timezone: f.timezone,
      address: null,
      storage_locations: [],
      departments: departments
        .filter((d) => d.facility_id === f.id)
        .map((d) => ({
          uid: newUid("d"),
          name: d.name,
          code: d.code,
          work_centers: workCenters
            .filter((w) => w.department_id === d.id)
            .map((w) => ({
              uid: newUid("w"),
              name: workCenterName(w, locale),
              // The bundle rides along UNRESOLVED, so a commit that does not
              // edit this name sends back exactly what it read.
              name_i18n: committedNameI18n(w),
              code: w.code,
              template_ref: null,
              machine_count: 0,
            })),
        })),
    })),
  };
}

// ---- immutable editing helpers ---------------------------------------------

export function emptyFacility(timezone: string): DraftFacility {
  return {
    uid: newUid("f"),
    name: "",
    code: "",
    timezone,
    address: null,
    storage_locations: [],
    departments: [],
  };
}

export function emptyDepartment(): DraftDepartment {
  return { uid: newUid("d"), name: "", code: "", work_centers: [] };
}

export function emptyWorkCenter(): DraftWorkCenter {
  // `name_i18n: null` — a node added here was never in the committed tree, so
  // its name is authored in the browser's locale and nothing else.
  return {
    uid: newUid("w"),
    name: "",
    name_i18n: null,
    code: "",
    template_ref: null,
    machine_count: 0,
  };
}

export function emptyStorageLocation(): DraftStorageLocation {
  return { uid: newUid("l"), name: "", code: "", parent_code: null };
}

/** Move `index` one slot toward `direction`, or return the list unchanged. */
export function moved<T>(list: readonly T[], index: number, direction: -1 | 1): T[] {
  const target = index + direction;
  if (index < 0 || index >= list.length || target < 0 || target >= list.length) return [...list];
  const next = [...list];
  const a = next[index];
  const b = next[target];
  if (a === undefined || b === undefined) return next;
  next[index] = b;
  next[target] = a;
  return next;
}
